
import { supabase } from "@/integrations/supabase/client";

export interface ImpersonationUser {
  id: string;
  username: string | null;
  email: string;
  display_name: string | null;
  is_subscriber: boolean;
}

/**
 * Fetches users that can be impersonated, categorized by subscription status
 */
export const fetchImpersonationUsers = async (): Promise<{ subscribers: ImpersonationUser[], nonSubscribers: ImpersonationUser[] }> => {
  try {
    // Use user_info view instead of joining tables manually
    // This view already combines user data with subscription status
    const { data, error } = await supabase
      .from('user_info')
      .select('user_id, username, email, display_name, is_subscriber, plan_name')
      .order('username');
    
    if (error) {
      console.error("Error fetching impersonation users:", error);
      return { subscribers: [], nonSubscribers: [] };
    }

    // Transform and categorize the users
    const subscribers: ImpersonationUser[] = [];
    const nonSubscribers: ImpersonationUser[] = [];

    data.forEach(user => {
      // Check if the user object exists AND has a user_id before proceeding.
      if (!user || !user.user_id) {
        console.warn("Skipping record due to missing user or user_id:", user);
        return; // Skip this iteration
      }

      // Now it's safe to access user properties
      const impersonationUser: ImpersonationUser = {
        id: user.user_id,
        username: user.username,
        email: user.email || '', // Ensure email is not null
        display_name: user.display_name,
        is_subscriber: !!user.is_subscriber // Convert to boolean
      };

      // Log user data for debugging (Now safe to access user.user_id)
      console.log(`User ${user.user_id}: is_subscriber=${user.is_subscriber}, plan=${user.plan_name}`);

      if (user.is_subscriber) {
        subscribers.push(impersonationUser);
      } else {
        nonSubscribers.push(impersonationUser);
      }
    });

    return { subscribers, nonSubscribers };
  } catch (error) {
    console.error("Unexpected error in fetchImpersonationUsers:", error);
    return { subscribers: [], nonSubscribers: [] };
  }
};

/**
 * Checks if the current user is an admin
 */
export const checkIsAdmin = async (): Promise<boolean> => {
  try {
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('role')
      .eq('id', (await supabase.auth.getUser()).data.user?.id)
      .single();
    
    if (userError || !userData) {
      console.error("Error checking admin status:", userError);
      return false;
    }

    return userData.role === 'admin';
  } catch (error) {
    console.error("Unexpected error in checkIsAdmin:", error);
    return false;
  }
};
